import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const LyrikaTosPage = () => {
  return (
    <div className="text-gray-100 font-sans relative selection:bg-fuchsia-500/30">
      <SEO
        title="Terms of Service — Lyrika-Stanza Discord Bot"
        description="Official Terms of Service for Lyrika-Stanza Discord bot, engineered by NotKoneko."
        path="/lyrika/tos"
        keywords={['Lyrika Terms of Service', 'Lyrika-Stanza ToS', 'Discord Bot Terms', 'NotKoneko', 'Koneko']}
      />
      <div className="max-w-4xl mx-auto relative z-10 space-y-8">
        {/* Navigation / Header */}
        <div className="flex items-center justify-between border-b border-purple-500/20 pb-6">
          <Link 
            to="/projects/discordbots/lyrika-stanza" 
            className="inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-fuchsia-300 transition-colors group"
          >
            <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Lyrika-Stanza Homepage
          </Link>
          <div className="flex items-center gap-2">
            <img src="/lyrika-avatar.webp" alt="Lyrika-Stanza" className="w-8 h-8 rounded-full border border-purple-500/40" />
            <span className="text-sm font-serif font-bold text-white">Lyrika-Stanza Legal</span>
          </div>
        </div>

        {/* Legal Document Container */}
        <div className="bg-[#090714] border border-purple-500/20 rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl space-y-6 sm:space-y-8">
          <div className="space-y-2 border-b border-purple-500/20 pb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/60 border border-purple-500/40 text-fuchsia-300 text-[11px] sm:text-xs font-mono">
              <span>📜</span> Official Legal Document for Discord App Verification
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif text-white tracking-wide">Terms of Service</h1>
            <p className="text-xs font-mono text-gray-400">Effective Date: June 27, 2026 • Host Infrastructure: Wispbyte Node Network</p>
          </div>

          <div className="prose prose-invert max-w-none text-gray-300 space-y-6 text-sm leading-relaxed font-sans">
            <p className="font-serif italic">
              — By inviting and using the <strong className="text-fuchsia-300 font-sans">Lyrika-Stanza</strong> bot in your Discord server, you (the server administrator and users) agree to the following Terms of Service (&quot;Terms&quot;). If you do not agree to these terms, please remove the bot from your server immediately.
            </p>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">1.</span> Description of Service
              </h2>
              <p>Lyrika-Stanza is a specialized Discord bot engineered to provide a time-synchronized scrolling lyrics visualization service in real-time. The bot operates by:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-300">
                <li>Automatically detecting songs played by your server&apos;s configured music bots (such as Chip, Jockie Music, or FredBoat).</li>
                <li>Streaming scrolling lyrics in real-time into a designated text channel with a highlighted focus frame (<code className="text-fuchsia-400">👉</code>).</li>
                <li>Allowing users to interactively adjust lyric synchronization latency using clickable inline Discord buttons (<code className="text-fuchsia-400">⏪ -0.5s</code> and <code className="text-fuchsia-400">⏩ +0.5s</code>).</li>
              </ul>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">2.</span> Required OAuth2 Scopes &amp; Bot Permissions
              </h2>
              <p>For Discord Application Verification and operational compliance, Lyrika-Stanza requests only the necessary permissions required to listen to music bot updates and display formatted lyric streams:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 font-mono text-xs">
                <div className="bg-[#090714] p-3 rounded-xl border border-purple-500/20">
                  <span className="text-fuchsia-400 font-bold block mb-1">OAuth2 Scopes:</span>
                  <ul className="list-disc pl-4 space-y-1 text-gray-300">
                    <li><code className="text-purple-300">applications.commands</code> (Slash commands)</li>
                    <li><code className="text-purple-300">bot</code> (Bot user functionality)</li>
                  </ul>
                </div>
                <div className="bg-[#090714] p-3 rounded-xl border border-purple-500/20">
                  <span className="text-pink-400 font-bold block mb-1">Guild Permissions:</span>
                  <ul className="list-disc pl-4 space-y-1 text-gray-300">
                    <li>View Channels &amp; Read Message History</li>
                    <li>Send Messages &amp; Embed Links</li>
                    <li>Manage Messages &amp; Add Reactions</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">3.</span> Data Collection and Operational Storage
              </h2>
              <p>To provide its core functionality, Lyrika-Stanza stores specific server configuration parameters in an isolated SQLite database hosted on Wispbyte infrastructure. Data collected is strictly operational:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-300 font-mono text-xs">
                <li><strong>Guild IDs:</strong> Unique Discord server identifier (<code className="text-fuchsia-400">guild_id</code>).</li>
                <li><strong>Listen Channel IDs:</strong> Assigned channel for detecting music announcements (<code className="text-fuchsia-400">listen_channel_id</code>).</li>
                <li><strong>Output Channel IDs:</strong> Assigned channel for displaying live lyrics (<code className="text-fuchsia-400">output_channel_id</code>).</li>
                <li><strong>Target Bot IDs:</strong> User ID of the music bot being tracked (<code className="text-fuchsia-400">target_bot_id</code>).</li>
                <li><strong>Sync Offsets:</strong> Latency synchronization preferences in milliseconds (<code className="text-fuchsia-400">sync_offset_ms</code>).</li>
              </ul>
              <p className="text-xs text-gray-400 pt-2 border-t border-purple-500/20 font-mono">
                <strong>Message Content Processing:</strong> The bot inspects message content strictly within configured listening channels to extract song title and artist information. It uses regex algorithms to strip markdown, custom emojis, and user mentions. Personal user messages are never recorded, logged, or stored.
              </p>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">4.</span> Third-Party API Integrations
              </h2>
              <p>Lyrika-Stanza integrates with the following external providers to fulfill service requests:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-300">
                <li><strong>Discord API:</strong> Facilitates WebSocket events and message dispatches under required privileged intents.</li>
                <li><strong>LRCLIB API (<code className="text-fuchsia-400">lrclib.net</code>):</strong> Transmits extracted song title queries over encrypted HTTPS to fetch time-synced lyric tracks. No user tokens or server IDs are shared with LRCLIB.</li>
              </ul>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">5.</span> User Responsibilities &amp; Access Control
              </h2>
              <p>Server administrators are responsible for executing slash setup commands (<code className="text-fuchsia-400">/setup-lyrics</code>, <code className="text-fuchsia-400">/set-targetbot</code>). Modifying server configurations requires the executing member to possess <code className="text-purple-300">Manage Channels</code> or <code className="text-purple-300">Administrator</code> permissions.</p>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">6.</span> Availability and Limitation of Liability
              </h2>
              <p>Lyrika-Stanza is provided on an &quot;as is&quot; and &quot;as available&quot; basis. We do not guarantee uninterrupted 100% uptime, nor do we guarantee lyric availability for every uncatalogued track. Compatibility depends on external API availability and music bot message consistency.</p>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">7.</span> Amendments to Terms
              </h2>
              <p>We reserve the right to revise these Terms at any time. Continued usage of Lyrika-Stanza following modifications constitutes agreement to the updated terms.</p>
            </section>
          </div>

          {/* Footer Navigation link */}
          <div className="pt-6 border-t border-purple-500/20 flex justify-between items-center text-xs font-mono">
            <Link to="/projects/discordbots/lyrika-stanza/privacy" className="text-fuchsia-400 hover:text-fuchsia-300 hover:underline">
              View Privacy Policy →
            </Link>
            <span className="text-gray-400">Lyrika-Stanza Legal Compliance</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LyrikaTosPage;
